import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {
    BdDunningPauseConfigReason,
    BdManualContractDecisionService
} from '../../../service/api/bd-manual-contract-decision.service';

/**
 * Information about an existing active pause (if any).
 */
interface ActivePauseInfo {
    id: number;
    reason: string | null;
    pauseUntil: string | null;
    comment: string | null;
    startedAt: string | null;
}

/**
 * AI:
 * Status: 'wave-3-implementation'
 * Type: Component
 * SubType: Exit-UI / Pause-Dialog
 * Reason: Formular zum Starten / Beenden einer Mahn-Pause. Die auswählbaren Gründe
 *         sowie die maximal zulässige Pausendauer werden beim Öffnen aus der
 *         Pause-Konfiguration des DunningType geladen (TM-1000001958). Bei aktiver
 *         Pause wird die Resume-Sektion eingeblendet.
 *
 *         Mapped to {@code BdDunningExitUiComponent.PAUSE_DIALOG}.
 *
 * @ticket TM-1000001786
 * @ticket TM-1000001958
 */
@Component({
    selector: 'bd-pause-dialog',
    templateUrl: './bd-pause-dialog.component.html',
    styleUrls: ['./bd-pause-dialog.component.scss'],
    standalone: false
})
export class BdPauseDialogComponent implements OnInit, OnDestroy {

    // ----------------------------------------------------------------- //
    // Inputs
    // ----------------------------------------------------------------- //

    @Input() dunning: Record<string, any> | null = null;
    @Input() uiComponentConfig: Record<string, any> | null = null;
    /**
     * Active pause (if any) — Wave 4 will inject this from the QL pipe; Wave 3
     * defaults to null which simply hides the resume section.
     */
    @Input() activePause: ActivePauseInfo | null = null;

    // ----------------------------------------------------------------- //
    // Outputs
    // ----------------------------------------------------------------- //

    @Output() actionSubmitted = new EventEmitter<{
        action: 'PAUSED' | 'RESUMED';
        generateConfirmationLetter?: boolean;
    }>();

    // ----------------------------------------------------------------- //
    // State
    // ----------------------------------------------------------------- //

    initialized: boolean = false;
    submitting: boolean = false;
    errorMessage: string | null = null;
    success: 'PAUSED' | 'RESUMED' | null = null;

    /**
     * TM-1000001958: Auswählbare, konfigurierbare Pause-Gründe
     * ({@code bd.DunningTypePauseReason}) des DunningType der Mahnung. Werden beim
     * Öffnen aus {@code GET /pause-config} geladen.
     */
    reasonOptions: BdDunningPauseConfigReason[] = [];
    configLoading: boolean = false;

    /** TM-1000001958: Gewählte Pause-Grund-Id (wird als {@code pauseReasonId} gesendet). */
    selectedReasonId: number | null = null;
    /** TM-1000001958: UI-Pflichtfehler, wenn kein Grund gewählt wurde. */
    reasonInvalid: boolean = false;

    pauseUntil: Date | null = null;
    comment: string = '';

    /**
     * C22: Optionale Checkbox "Bestätigungsschreiben erzeugen". Wird beim
     * Pausen-Start an die Hostseite/Historie weitergereicht (rein FE-seitiges
     * Flag — kein erfundenes Backend-Enforcement).
     */
    generateConfirmationLetter: boolean = false;

    /**
     * TM-1000001958: Maximal zulässige Pausendauer in Tagen, geladen aus der
     * Pause-Konfiguration des DunningType. {@code null} bedeutet unbegrenzt.
     * Steuert das {@code maxDate} des Datepickers und erzwingt — sofern gesetzt —
     * eine befristete Pause (eine unbefristete Pause lehnt das Backend dann ab).
     */
    maxPauseDays: number | null = null;

    /** TM-1000001958: Berechnetes Höchstdatum für den Datepicker (heute + maxPauseDays). */
    maxPauseDate: Date | null = null;

    private configSubscription: Subscription | null = null;
    private submitSubscription: Subscription | null = null;
    private resumeSubscription: Subscription | null = null;

    constructor(private readonly manualContractDecisionService: BdManualContractDecisionService) {}

    // ----------------------------------------------------------------- //
    // Lifecycle
    // ----------------------------------------------------------------- //

    ngOnInit(): void {
        this.loadPauseConfig();
        this.initialized = true;
    }

    ngOnDestroy(): void {
        this.configSubscription?.unsubscribe();
        this.submitSubscription?.unsubscribe();
        this.resumeSubscription?.unsubscribe();
    }

    // ----------------------------------------------------------------- //
    // Config loading (TM-1000001958)
    // ----------------------------------------------------------------- //

    /**
     * TM-1000001958: Lädt beim Öffnen die Pause-Konfiguration (max. Dauer +
     * konfigurierbare Gründe) des DunningType der Mahnung.
     */
    private loadPauseConfig(): void {
        const dunningId = this.dunning?.['id'] ?? this.dunning?.['dunningId'];
        if (dunningId == null) {
            return;
        }
        this.configLoading = true;
        this.configSubscription?.unsubscribe();
        this.configSubscription = this.manualContractDecisionService.getPauseConfig(dunningId).subscribe({
            next: (config) => {
                this.reasonOptions = config?.reasons ?? [];
                this.maxPauseDays = config?.maxPauseDays ?? null;
                this.maxPauseDate = this.computeMaxPauseDate();
                // Bei gesetztem Maximum eine unbefristete Pause vermeiden: Datum
                // auf das Höchstdatum vorbelegen, falls noch keines gewählt wurde.
                if (this.maxPauseDays != null && this.pauseUntil == null) {
                    this.pauseUntil = this.maxPauseDate;
                }
                this.configLoading = false;
            },
            error: () => {
                this.reasonOptions = [];
                this.maxPauseDays = null;
                this.maxPauseDate = null;
                this.configLoading = false;
            }
        });
    }

    /** TM-1000001958: Höchstdatum (heute + maxPauseDays) oder {@code null} bei unbegrenzt. */
    private computeMaxPauseDate(): Date | null {
        if (this.maxPauseDays == null) {
            return null;
        }
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + this.maxPauseDays);
        return d;
    }

    // ----------------------------------------------------------------- //
    // Computed
    // ----------------------------------------------------------------- //

    /**
     * TM-1000001958: Bei gesetztem Maximum ist ein "Pause bis"-Datum Pflicht,
     * da das Backend eine unbefristete Pause dann ablehnt.
     */
    get pauseUntilRequired(): boolean {
        return this.maxPauseDays != null;
    }

    get hasActivePause(): boolean {
        return this.activePause != null && this.activePause.id != null;
    }

    // ----------------------------------------------------------------- //
    // Actions
    // ----------------------------------------------------------------- //

    startPause(): void {
        const dunningId = this.dunning?.['id'] ?? this.dunning?.['dunningId'];
        if (dunningId == null) {
            this.errorMessage = 'Keine Mahnung referenziert.';
            return;
        }
        // TM-1000001958: Grund-Pflicht — leerer Wert blockiert das Pausieren.
        this.reasonInvalid = this.selectedReasonId == null;
        if (this.reasonInvalid) {
            this.errorMessage = 'Bitte einen Pausen-Grund auswählen.';
            return;
        }
        // TM-1000001958: Bei gesetztem Maximum ist ein "Pause bis"-Datum Pflicht
        // (eine unbefristete Pause lehnt das Backend dann ab).
        if (this.pauseUntilRequired && !this.pauseUntil) {
            this.errorMessage = 'Bitte ein "Pause bis"-Datum auswählen — eine unbefristete '
                + 'Pause ist bei konfiguriertem Maximum nicht zulässig.';
            return;
        }
        this.errorMessage = null;
        this.submitting = true;

        // TM-1000001958: Das gewählte Datum unverändert übernehmen; ohne gesetztes
        // Maximum darf die Pause unbefristet bleiben (kein Datum gewählt → null).
        const pauseUntilIso = this.pauseUntil ? this.toIsoDate(this.pauseUntil) : null;

        this.submitSubscription?.unsubscribe();
        this.submitSubscription = this.manualContractDecisionService.pauseDunning(
            dunningId,
            null,
            pauseUntilIso,
            this.comment?.trim() || null,
            this.selectedReasonId
        ).subscribe({
            next: () => {
                this.submitting = false;
                this.success = 'PAUSED';
                this.actionSubmitted.emit({
                    action: 'PAUSED',
                    generateConfirmationLetter: this.generateConfirmationLetter
                });
            },
            error: (err) => {
                this.submitting = false;
                this.errorMessage = this.extractError(err);
            }
        });
    }

    endPause(): void {
        const dunningId = this.dunning?.['id'] ?? this.dunning?.['dunningId'];
        if (dunningId == null || !this.activePause) {
            this.errorMessage = 'Keine aktive Pause zum Beenden.';
            return;
        }
        this.errorMessage = null;
        this.submitting = true;

        this.resumeSubscription?.unsubscribe();
        this.resumeSubscription = this.manualContractDecisionService
            .resumeDunning(dunningId, this.activePause.id).subscribe({
            next: () => {
                this.submitting = false;
                this.success = 'RESUMED';
                this.actionSubmitted.emit({action: 'RESUMED'});
            },
            error: (err) => {
                this.submitting = false;
                this.errorMessage = this.extractError(err);
            }
        });
    }

    // ----------------------------------------------------------------- //
    // Internal helpers
    // ----------------------------------------------------------------- //

    private toIsoDate(d: Date): string {
        // yyyy-MM-dd format for the backend
        const yyyy = d.getFullYear();
        const mm = `${d.getMonth() + 1}`.padStart(2, '0');
        const dd = `${d.getDate()}`.padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    /**
     * TM-1000001958: Liefert die deutsche Backend-Fehlermeldung (z. B. bei
     * Überschreitung des Maximums oder unbefristeter Pause trotz Maximum).
     */
    private extractError(err: unknown): string {
        if (err && typeof err === 'object') {
            const anyErr = err as any;
            return anyErr.error?.error
                ?? anyErr.error?.message
                ?? anyErr.message
                ?? 'Unbekannter Fehler.';
        }
        return 'Unbekannter Fehler.';
    }
}
